/**
 * Test stimă de sine. Rosenberg Self-Esteem Scale (RSES).
 * Rosenberg, M. (1965). Society and the Adolescent Self-Image. Princeton
 * University Press. APA PsycTESTS record: DOI 10.1037/t01038-000.
 *
 * Licence: public domain, per the University of Maryland Department of
 * Sociology ("you may use it without charge and without notifying the
 * Sociology Department", including translations and adaptations). No
 * commercial/non-commercial distinction. Verified in
 * monorepo/seo-report/briefs/instrumente-stare-de-bine.md and re-read on
 * socy.umd.edu on 2026-09-13.
 *
 * Romanian: no validated Romanian version was found. The wording below is a
 * Mindtale adaptation and the page says so.
 *
 * ── Scoring, checked against socy.umd.edu/about-us/using-rosenberg-self-esteem-scale
 * Item order is the UMD order. Each answer is stored as its RAW agreement
 * value (Total de acord = 3 … Total dezacord = 0), the same for every item,
 * so the runner can use one option list. `categorize` then reverses the five
 * negatively worded items (3, 5, 8, 9, 10): for those, agreeing means lower
 * self-esteem, so the item score is 3 − raw. Forgetting this step is the
 * classic RSES bug: it drags every score towards the middle, since the
 * negative items then cancel the positive ones.
 *   Positive items 1, 2, 4, 6, 7:  SA 3 · A 2 · D 1 · SD 0
 *   Negative items 3, 5, 8, 9, 10: SA 0 · A 1 · D 2 · SD 3
 * Total 0–30. Higher = higher self-esteem.
 *
 * Cut-off: UMD's own FAQ says there are no discrete cut-off points. The page
 * reads the score against 15, the midpoint of 0–30 and the conventional
 * reading, and says plainly that it is a convention, not Rosenberg's.
 */

import type { TestConfig } from "./types";

export type SelfEsteemCategory = "lower" | "higher";

/** The negatively worded items, by id. Scored in reverse. */
export const REVERSED_ITEM_IDS: readonly number[] = [3, 5, 8, 9, 10];

/** Below this total, the result reads as low self-esteem for now. */
export const SELF_ESTEEM_MIDPOINT = 15;

const QUESTIONS = [
  { id: 1, text: "Simt că sunt un om valoros, cel puțin la fel ca ceilalți." },
  { id: 2, text: "Simt că am o mulțime de calități bune." },
  { id: 3, text: "Una peste alta, înclin să cred că sunt un eșec." },
  { id: 4, text: "Pot face lucrurile la fel de bine ca majoritatea oamenilor." },
  { id: 5, text: "Simt că nu am prea multe lucruri cu care să mă mândresc." },
  { id: 6, text: "Am o atitudine pozitivă față de mine." },
  { id: 7, text: "În general, sunt mulțumit/ă de mine." },
  { id: 8, text: "Mi-aș dori să am mai mult respect față de mine." },
  { id: 9, text: "Uneori mă simt cu adevărat inutil/ă." },
  { id: 10, text: "Uneori cred că nu sunt bun/ă de nimic." },
];

/* Raw agreement values. NOT item scores: see the note at the top. */
const OPTIONS = [
  { label: "Total de acord", value: 3 },
  { label: "De acord", value: 2 },
  { label: "Dezacord", value: 1 },
  { label: "Total dezacord", value: 0 },
];

/** Points one raw answer contributes, after reverse scoring. */
export function scoreItem(itemId: number, raw: number): number {
  return REVERSED_ITEM_IDS.includes(itemId) ? 3 - raw : raw;
}

const THERAPIST_URL =
  "https://mindtale.ro/gaseste-terapeut?energy=2&utm_source=email&utm_medium=transactional&utm_campaign=test_stima_de_sine";
const GUIDE_URL =
  "https://mindtale.ro/stima-de-sine?utm_source=email&utm_medium=transactional&utm_campaign=test_stima_de_sine";

export const stimaDeSineConfig: TestConfig<SelfEsteemCategory> = {
  slug: "stima-de-sine",
  totalQuestions: QUESTIONS.length,
  estimatedMinutes: 2,
  /* Only the three title fields are rendered (as the H1). The rest of
     `hero` belongs to the old hero component, which this page does not use. */
  hero: {
    eyebrow: "10 întrebări · 2 minute · gratuit",
    titlePart1: "Test stimă de sine:\ncum ",
    italicText: "te privești",
    titlePart2: " acum?",
    subtitle:
      "Zece afirmații din Scala Rosenberg despre felul în care te privești.",
    badges: [],
    imageSrc: "",
    imageAlt: "",
  },
  questions: QUESTIONS,
  options: OPTIONS,
  scoring: {
    type: "sum",
    scoreMax: 30,
    scoreSuffix: "/30",
    allowedAnswerValues: [0, 1, 2, 3],
    categorize: (answers) => {
      if (answers.length !== QUESTIONS.length) {
        throw new Error("Toate întrebările trebuie completate");
      }
      const score = answers.reduce(
        (acc, raw, i) => acc + scoreItem(QUESTIONS[i].id, raw),
        0,
      );
      const category: SelfEsteemCategory =
        score < SELF_ESTEEM_MIDPOINT ? "lower" : "higher";
      return { score, category };
    },
  },
  bands: [
    {
      category: "lower",
      range: "0–14",
      label: "Sub jumătatea scalei",
      caption: "În perioada asta te privești cu multă asprime.",
      bg: "#E0B96C",
    },
    {
      category: "higher",
      range: "15–30",
      label: "De la jumătatea scalei în sus",
      caption: "În general, te privești cu îngăduință.",
      bg: "#7DA86F",
    },
  ],
  resultCopy: {
    lower: {
      headline: "În perioada asta te privești cu multă asprime",
      subheadline: "Scorul arată cum te vezi acum, nu cât valorezi.",
      description:
        "Răspunsurile tale spun că acum ți-e greu să vezi ce e bun în tine și că vocea care te critică are mult loc. Mulți oameni trec prin asta, iar felul în care ne privim se schimbă în timp. Rezultatul descrie o perioadă, nu cine ești.",
      meaning:
        "Scorul tău e sub 15, jumătatea scalei de la 0 la 30. Scala Rosenberg nu are un prag oficial, dar un scor în zona asta sugerează o stimă de sine scăzută în acest moment. Nu e un diagnostic și nu spune de unde vine felul în care te privești. Asta se lămurește într-o discuție cu un psihoterapeut, iar ce urmează după ea hotărăști împreună cu el.",
      actions: [
        "Notează o frază critică pe care ți-o spui des, exact cum sună în mintea ta. Scrisă pe hârtie, e mai ușor de cântărit decât atunci când doar trece prin minte.",
        "Gândește-te ce i-ai spune unui prieten apropiat în aceeași situație, cu aceleași fapte, și încearcă să îți spui ție ceva asemănător.",
        "Dacă felul în care te privești îți ia de câteva săptămâni din somn, din muncă sau din relații, merită o discuție cu un psihoterapeut. Nu trebuie să știi dinainte ce să spui.",
      ],
      ctaLabel: "Vezi psihoterapeuți care lucrează cu stima de sine",
      ctaTone: "warm",
      showTherapistMatch: true,
    },
    higher: {
      headline: "În general, te privești cu îngăduință",
      subheadline: "Îți recunoști calitățile și te accepți, în mare, așa cum ești.",
      description:
        "Răspunsurile tale arată că, în linii mari, te vezi ca un om valoros și te tratezi cu respect. Asta nu înseamnă că nu ai zile în care te îndoiești de tine. Înseamnă că, de obicei, îndoiala nu are ultimul cuvânt.",
      meaning:
        "Scorul tău e de cel puțin 15, jumătatea scalei de la 0 la 30. Scala măsoară felul general în care te privești, nu cum te simți în fiecare zonă a vieții. Dacă ceva anume te apasă, un scor în zona asta nu înseamnă că nu merită să vorbești cu cineva despre el.",
      actions: [
        "Păstrează rezultatul și refă testul peste câteva luni, mai ales după o perioadă grea. Felul în care ne vedem se schimbă odată cu ce trăim.",
        "Observă ce te ajută să te porți frumos cu tine când greșești. Merită ținut minte pentru momentele în care îți va fi mai greu.",
        "Dacă o voce critică revine în anumite situații, la muncă sau în relații, poți lucra pe asta cu un psihoterapeut, chiar dacă scorul total nu e mic.",
      ],
      ctaLabel: "Citește ghidul despre stima de sine",
      ctaTone: "soft",
      showTherapistMatch: false,
    },
  },
  source: {
    name: "Rosenberg Self-Esteem Scale (RSES)",
    citationTitle: "Society and the Adolescent Self-Image.",
    authors: "Rosenberg M",
    journal: "Princeton University Press",
    year: 1965,
    volume: "APA PsycTESTS",
    doi: "10.1037/t01038-000",
    doiUrl: "https://doi.org/10.1037/t01038-000",
    license: "Domeniu public (University of Maryland, Department of Sociology)",
    subsetNote:
      "Cei 10 itemi sunt scala completă. Varianta în română este o adaptare Mindtale, nevalidată separat pe populația din România.",
  },
  /* Required by the type, rendered nowhere (the layout dropped the stat
     cards). Rosenberg's 1965 sample of New York State high-school students;
     not re-verified in this session, so do not surface it without a source. */
  validity: {
    sampleSize: 5024,
  },
  ui: {
    primaryAccent: "#DC7050",
    badgeDot: "#DC7050",
  },
  email: {
    subjectTemplate: (score, scoreMax, label) =>
      `Rezultatul tău la testul de stimă de sine: ${score}/${scoreMax} (${label})`,
    categoryStyles: {
      lower: {
        label: "Sub jumătatea scalei",
        range: "0–14",
        accent: "#A87D5C",
        barBg: "#F4E8D9",
        barFg: "#264653",
        ctaLabel: "Vezi psihoterapeuți recomandați",
        ctaUrl: THERAPIST_URL,
      },
      higher: {
        label: "De la jumătatea scalei în sus",
        range: "15–30",
        accent: "#5D8451",
        barBg: "#E8F0EA",
        barFg: "#264653",
        ctaLabel: "Citește ghidul despre stima de sine",
        ctaUrl: GUIDE_URL,
      },
    },
  },
  emailStarter: {
    title: "Ce poți încerca, indiferent de scor",
    intro:
      "Nu sunt afirmații pozitive. Pentru cine se privește aspru, acelea pot face mai mult rău decât bine. Sunt mici exerciții despre felul în care te tratezi, nu despre cât de bine te evaluezi.",
    exercises: [
      {
        name: "Prinde fraza exactă",
        how: "Când simți că te critici, scrie ce spune vocea, cuvânt cu cuvânt. O critică vagă pare un adevăr despre tine. Una scrisă e o propoziție pe care o poți cântări.",
      },
      {
        name: "Ce i-ai spune unui prieten",
        how: "Ia aceeași situație și imaginează-ți că i s-a întâmplat unui prieten apropiat. Scrie ce i-ai spune lui, apoi citește-ți-o ție.",
      },
      {
        name: "O frază adevărată în loc de una frumoasă",
        how: "În loc de „sunt minunat/ă”, dacă azi nu crezi asta, încearcă „e greu acum și nu sunt singurul om căruia i se întâmplă”. O frază pe care n-ai de ce s-o contrazici.",
      },
    ],
    suggestions: [
      "Observă în ce situații revine vocea critică: la muncă, în relații, când ești obosit/ă.",
      "Dacă după câteva săptămâni e la fel de tare și îți ia din somn sau din relații, vorbește cu un psihoterapeut.",
    ],
    book: "„Self-Compassion” de Kristin Neff",
  },
  therapistCTA: {
    utmMedium: "test_stima_de_sine",
    triggerCategories: ["lower"],
    blocks: {
      lower: {
        headline: "Nu trebuie să schimbi singur/ă felul în care te privești",
        subline:
          "Iată câțiva psihoterapeuți care lucrează cu stima de sine și cu autocritica. Prima întâlnire e un început, iar tu decizi dacă mergi mai departe.",
        ctaLabel: "Vezi psihoterapeuții",
        energyParam: 2,
      },
    },
  },
  meta: {
    title: "Test stimă de sine (Scala Rosenberg): cum te privești? | Mindtale",
    description:
      "10 afirmații din Scala Rosenberg, 2 minute, gratuit. Afli cum te privești în perioada asta, ce înseamnă scorul și ce nu poate spune un test.",
    canonicalUrl: "https://mindtale.ro/teste/stima-de-sine",
    keywords: [
      "test stima de sine",
      "test stima de sine online",
      "scala rosenberg",
      "scala rosenberg stima de sine",
      "test rosenberg",
      "test stima de sine gratuit",
      "stima de sine scazuta test",
      "test incredere in sine",
    ],
  },
  stepperFootnote:
    "Răspunde după cum te vezi în general, în perioada asta. Nu există răspunsuri „corecte” sau „greșite”.",
  /* Not rendered: TestRunner has no intro card. Kept because the type needs it. */
  intro: {
    eyebrow: "10 întrebări · 2 minute · gratuit",
    title: "Răspunde după cum te vezi în general.",
    subtitle: "Nu există răspunsuri corecte.",
    ctaLabel: "Începe testul",
  },
  bandsHeader: "Cele două zone",
};
